import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, TrendingUp, TrendingDown, FileText, Receipt, IndianRupee, Banknote, Wallet, History as HistoryIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BudgetHeadDetail = () => {
  const { budgetHeadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [budgetHead, setBudgetHead] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({
    totalPOs: 0,
    totalInvoices: 0,
    totalInvoiced: 0,
    totalPaid: 0,
    totalDue: 0,
    totalPayments: 0,
  });

  useEffect(() => {
    fetchBudgetHeadDetails();
  }, [budgetHeadId]);

  const fetchBudgetHeadDetails = async () => {
    setLoading(true);
    try {
      // Get budget head details
      const budgetResponse = await axios.get(`${API}/budgets/${budgetHeadId}`, {
        withCredentials: true,
      });
      setBudgetHead(budgetResponse.data);

      // Get POs for this budget head
      const posResponse = await axios.get(
        `${API}/purchase-orders?fiscal_year=${budgetResponse.data.fiscal_year}`,
        { withCredentials: true }
      );
      const filteredPOs = posResponse.data.filter(
        (po) => po.budget_head === budgetResponse.data.budget_head
      );
      setPurchaseOrders(filteredPOs);

      // Get invoices for these POs
      const poIds = filteredPOs.map((po) => po.id);
      const invoicesResponse = await axios.get(
        `${API}/invoices?fiscal_year=${budgetResponse.data.fiscal_year}`,
        { withCredentials: true }
      );
      const filteredInvoices = invoicesResponse.data.filter((inv) =>
        poIds.includes(inv.po_id)
      );
      setInvoices(filteredInvoices);

      // Get all payments and filter those allocated to invoices of this budget head
      const invoiceIds = filteredInvoices.map((inv) => inv.id);
      const paymentsResponse = await axios.get(`${API}/payments`, {
        withCredentials: true,
      });
      const filteredPayments = paymentsResponse.data.filter((payment) => {
        // Check if any allocated invoice belongs to this budget head
        return payment.allocated_invoices?.some((alloc) =>
          invoiceIds.includes(alloc.invoice_id)
        );
      });
      setPayments(filteredPayments);

      // Calculate stats
      const totalInvoiced = filteredInvoices.reduce(
        (sum, inv) => sum + (inv.taxable_value || 0),
        0
      );
      const totalPaid = filteredInvoices.reduce(
        (sum, inv) => sum + (inv.paid_amount || 0),
        0
      );
      const totalDue = totalInvoiced - totalPaid;

      // Calculate total payments for this budget head
      const totalPayments = filteredPayments.reduce((sum, payment) => {
        const allocatedToThisBudget = payment.allocated_invoices
          ?.filter((alloc) => invoiceIds.includes(alloc.invoice_id))
          .reduce((s, alloc) => s + (alloc.allocated_amount || 0), 0) || 0;
        return sum + allocatedToThisBudget;
      }, 0);

      setStats({
        totalPOs: filteredPOs.length,
        totalInvoices: filteredInvoices.length,
        totalInvoiced,
        totalPaid,
        totalDue,
        totalPayments,
      });

      // Fetch the audit log — non-blocking, best-effort. Older budget docs
      // won't have an audit_log array yet; the endpoint returns an empty
      // list and the UI shows a "No edits yet" placeholder.
      try {
        const auditRes = await axios.get(
          `${API}/budgets/${budgetHeadId}/audit-log`,
          { withCredentials: true }
        );
        setAuditLog(auditRes.data?.entries || []);
      } catch (e) {
        // Silently degrade — main detail view still works.
        setAuditLog([]);
      }
    } catch (error) {
      toast.error('Failed to load budget head details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!budgetHead) {
    return (
      <div className="p-8">
        <p>Budget head not found</p>
        <Button onClick={() => navigate('/dashboard')} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const consumedPercentage =
    (budgetHead.consumed_amount / budgetHead.allocated_amount) * 100;
  const remainingAmount = budgetHead.allocated_amount - budgetHead.consumed_amount;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl font-heading font-bold tracking-tight">
            {budgetHead.budget_head}
          </h1>
          <p className="text-muted-foreground mt-1">
            {budgetHead.department} • FY {budgetHead.fiscal_year}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Allocated Budget</CardTitle>
              <IndianRupee className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ₹{budgetHead.allocated_amount.toLocaleString('en-IN')}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Consumed (POs)</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-accent">
                ₹{budgetHead.consumed_amount.toLocaleString('en-IN')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {consumedPercentage.toFixed(1)}% consumed
              </p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Remaining</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">
                ₹{remainingAmount.toLocaleString('en-IN')}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-gradient-to-br from-purple-500/10 to-purple-600/5">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Invoiced</CardTitle>
              <Receipt className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                ₹{stats.totalInvoiced.toLocaleString('en-IN')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.totalInvoices} invoices
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-gradient-to-br from-green-500/10 to-green-600/5">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Payments Made</CardTitle>
              <Banknote className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ₹{stats.totalPaid.toLocaleString('en-IN')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {payments.length} payments
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-gradient-to-br from-red-500/10 to-red-600/5">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Due Payments</CardTitle>
              <Wallet className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                ₹{stats.totalDue.toLocaleString('en-IN')}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle>Budget Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Progress value={consumedPercentage} className="h-4" />
              <div className="flex justify-between text-sm">
                <span>₹{budgetHead.consumed_amount.toLocaleString('en-IN')}</span>
                <span className="font-semibold">{consumedPercentage.toFixed(1)}%</span>
                <span>₹{budgetHead.allocated_amount.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Purchase Orders */}
        <Card className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Purchase Orders ({stats.totalPOs})
            </CardTitle>
            <CardDescription>All POs issued under this budget head</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {purchaseOrders.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No purchase orders found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                      <th className="text-left py-3 px-4">PO Number</th>
                      <th className="text-left py-3 px-4">Vendor</th>
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-right py-3 px-4">Taxable Value</th>
                      <th className="text-right py-3 px-4">Open Value</th>
                      <th className="text-right py-3 px-4">Net Payable</th>
                      <th className="text-center py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.map((po) => (
                      <tr key={po.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4 font-mono text-sm">{po.po_number}</td>
                        <td className="py-3 px-4">{po.vendor_name}</td>
                        <td className="py-3 px-4 text-sm">
                          {new Date(po.date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          ₹{po.taxable_value?.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-right text-success font-medium">
                          ₹{po.open_value?.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-right font-bold">
                          ₹{po.net_payable?.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate('/purchase-orders')}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoices */}
        <Card className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Invoices ({stats.totalInvoices})
            </CardTitle>
            <CardDescription>All invoices received under this budget head</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {invoices.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No invoices found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                      <th className="text-left py-3 px-4">Invoice #</th>
                      <th className="text-left py-3 px-4">PO Number</th>
                      <th className="text-left py-3 px-4">Vendor</th>
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-right py-3 px-4">Taxable Value</th>
                      <th className="text-right py-3 px-4">Net Payable</th>
                      <th className="text-right py-3 px-4">Paid Amount</th>
                      <th className="text-right py-3 px-4">Due Amount</th>
                      <th className="text-center py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4 font-mono text-sm">{invoice.invoice_number}</td>
                        <td className="py-3 px-4 font-mono text-sm">{invoice.po_number}</td>
                        <td className="py-3 px-4">{invoice.vendor_name}</td>
                        <td className="py-3 px-4 text-sm">
                          {new Date(invoice.date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          ₹{invoice.taxable_value?.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-right font-bold">
                          ₹{invoice.net_payable?.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-right text-success font-medium">
                          ₹{invoice.paid_amount?.toLocaleString('en-IN') || 0}
                        </td>
                        <td className="py-3 px-4 text-right text-destructive font-medium">
                          ₹{((invoice.net_payable || 0) - (invoice.paid_amount || 0)).toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate('/invoices')}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payments */}
        <Card className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              Bank Payments ({payments.length})
            </CardTitle>
            <CardDescription>All payments made against invoices of this budget head</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {payments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No payments found for this budget head</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Party Name</th>
                      <th className="text-left py-3 px-4">Narration</th>
                      <th className="text-right py-3 px-4">Amount</th>
                      <th className="text-left py-3 px-4">Payment Mode</th>
                      <th className="text-left py-3 px-4">Reference</th>
                      <th className="text-center py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4 text-sm">
                          {new Date(payment.date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 font-medium">{payment.party_name}</td>
                        <td className="py-3 px-4 text-sm">{payment.narration}</td>
                        <td className="py-3 px-4 text-right font-bold text-primary">
                          ₹{payment.amount?.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 text-sm">{payment.payment_mode}</td>
                        <td className="py-3 px-4 text-sm font-mono">
                          {payment.reference_number || '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate('/bank')}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit History — audit trail of who changed what and when. */}
        <Card className="border-border" data-testid="budget-audit-log-card">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center gap-2">
              <HistoryIcon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              <CardTitle className="font-heading">Edit History</CardTitle>
            </div>
            <CardDescription>
              Every edit to this budget head — including leg-split changes — with
              the user and timestamp.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {auditLog.length === 0 ? (
              <div className="text-center py-8">
                <HistoryIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">
                  No edits recorded yet for this budget head.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Future edits via the Dashboard or Budget Upload screens will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLog.map((entry, idx) => {
                  const when = entry.at ? new Date(entry.at) : null;
                  const fields = Object.entries(entry.changes || {});
                  return (
                    <div
                      key={idx}
                      className="rounded-lg border border-border bg-muted/20 p-4"
                      data-testid={`audit-entry-${idx}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {entry.action === 'leg_split_edit' ? 'Leg-split edit' : 'Edit'}
                          </Badge>
                          <span className="text-sm font-medium">
                            {entry.by_user_name || entry.by_user_email || 'Unknown user'}
                          </span>
                          {entry.by_user_email && entry.by_user_name !== entry.by_user_email && (
                            <span className="text-xs text-muted-foreground">
                              ({entry.by_user_email})
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {when ? when.toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }) : '—'}
                        </span>
                      </div>
                      {fields.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">
                          No field-level diff captured.
                        </p>
                      ) : (
                        <div className="grid gap-1.5">
                          {fields.map(([field, diff]) => {
                            const before = diff?.before;
                            const after = diff?.after;
                            const isAmount =
                              field.startsWith('alloc_') || field === 'allocated_amount';
                            const fmt = (v) => {
                              if (v === null || v === undefined || v === '') return '—';
                              if (isAmount) {
                                const n = Number(v);
                                return Number.isFinite(n)
                                  ? `₹${n.toLocaleString('en-IN')}`
                                  : String(v);
                              }
                              return String(v);
                            };
                            return (
                              <div
                                key={field}
                                className="flex flex-wrap items-center gap-2 text-xs"
                              >
                                <span className="font-mono text-muted-foreground min-w-[140px]">
                                  {field}
                                </span>
                                <span className="line-through text-red-600/80">
                                  {fmt(before)}
                                </span>
                                <span className="text-muted-foreground">→</span>
                                <span className="font-semibold text-emerald-700">
                                  {fmt(after)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BudgetHeadDetail;
